import CallWidget from 'flow/components/call-components/call-widget/CallWidget';
import CtiIntegrationWidget from '../call-components/call-widgets/CtiIntegrationWidget';
import AvatarDescription from './AvatarDescription';
import ButtonContainer from 'flow/components/communication-canvas/ButtonContainer';
import ExternalSearchWidget from 'flow/components/communication-canvas/CallExternalSearchWidget';
import CallTitle from 'flow/components/communication-canvas/CallTitle';
import CentionErrandWidget from './CentionErrandWidget';
import CoListeningSubjectBanner from 'flow/components/communication-canvas/CoListeningSubjectBanner';
import RedirectPresentationWidget from 'flow/components/communication-canvas/RedirectPresentationWidget';
import TitleAndSubtitle from 'flow/components/communication-canvas/TitleSubtitle';
import useAppContext from 'flow/hooks/useAppContext';
import useMediaQuery from 'flow/hooks/useMediaQuery';
import { Agent } from 'flow/types/CallRecord';
import { Contact } from 'flow/types/Contact';
import { TypedMap } from 'flow/types/TypedMap';
import CallAvatar from './CallAvatar';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { discriminatorIsPresent } from 'flow/utils/call-widget-utils';
import {
    getCallItemContactExtra,
    getCallItemContactSubtitle,
    getCallItemContactTitle,
} from 'flow/utils/contact-utils';
import {
    getSortedExternalSearchCallWidgetUrlsWithPhoneNumbers,
    getVersion2Widgets,
} from 'flow/utils/run-url-utils';
import { twMerge } from 'tailwind-merge';
import React, { useEffect, useMemo } from 'react';
import { isAppCti } from 'flow/utils/app-utils';
import { getCallWidgetVariables } from '../../selectors/call-widget-selectors';
import { getQueueNumberFromQueueId } from '../../selectors/selectors';
import OmniTicketWidget from './OmniTicketWidget';
import EidWidget from 'flow/components/call-components/call-widgets/EidWidget';
import { useAppSelector } from 'flow/hooks/useRedux';
import { ActiveLineData } from 'flow/types/ActiveLineData';
import { CallWidget as CallWidgetState, Config } from 'flow/types/CallWidget';
import PinCode from './PinCode';

interface Props {
    callId?: string;
    direction: string;
    remoteNumber: string;
    localNumber?: string;
    ownCountryCode: string;
    startTime?: Date;
    isOnHold: boolean;
    isAccepted: boolean;
    isEnded: boolean;
    failCause?: string;
    contact: TypedMap<Contact>;
    onAnswer?: () => void;
    onHangup?: () => void;
    onHold?: () => void;
    onUnhold?: () => void;
    onMerge?: () => void;
    onTransferStart?: () => void;
    onDecline?: () => void;
    onTransferComplete?: () => void;
    onTransferCancel?: () => void;
    conferenceToMeeting?: () => void;
    getPinCode?: (meetingCode: string) => Promise<void>;
    isConference?: boolean;
    centionDeeplink?: string;
    conferenceNumber?: string;
    conferencePinCode?: string;
    conferenceMeetingCode?: string;
    agentInfo?: TypedMap<Agent>;
    activeCall?: TypedMap<ActiveLineData>;
}

interface ExternalSearchUrlData {
    key: string;
    url: string;
    title: string;
}

const LargeCallItem = ({
    callId,
    direction,
    failCause,
    remoteNumber,
    localNumber,
    ownCountryCode,
    startTime,
    isOnHold,
    contact,
    isAccepted,
    isEnded,
    onAnswer,
    onHangup,
    onDecline,
    onHold,
    onUnhold,
    onTransferStart,
    onTransferComplete,
    onTransferCancel,
    onMerge,
    agentInfo,
    activeCall,
    isConference = false,
    conferenceNumber,
    conferenceMeetingCode,
    conferencePinCode,
    getPinCode,
    conferenceToMeeting,
    centionDeeplink,
}: Props) => {
    const { i18next } = useAppContext();

    const showLargeContact = useMediaQuery('(min-height:712px)');

    const callWidgets = useAppSelector((state) => state.callWidgets);
    const showCtiWidget = useAppSelector(isAppCti);

    const queueId = activeCall?.get('queue');
    const queueNumber = useAppSelector((state) =>
        getQueueNumberFromQueueId(state, queueId),
    );
    const variables = useAppSelector((state) =>
        getCallWidgetVariables(state, activeCall, queueNumber),
    );
    const omniTicketId = variables?.['telavox.omni.ticket_id'];

    const externalSearchUrls = useMemo(
        () =>
            getSortedExternalSearchCallWidgetUrlsWithPhoneNumbers(
                callWidgets,
                variables,
            ),
        [callWidgets, variables],
    );

    const title = getCallItemContactTitle(
        contact,
        agentInfo,
        remoteNumber,
        ownCountryCode,
        i18next,
    ) || { text: '', type: '' };
    const subtitle = getCallItemContactSubtitle(
        title,
        contact,
        remoteNumber,
        ownCountryCode,
        i18next,
    ) || { text: '', type: '' };
    const extraInfo = getCallItemContactExtra(
        title,
        subtitle,
        contact,
        remoteNumber,
        ownCountryCode,
    ) || { text: '', type: '' };

    const callWidgetVersion2: TypedMap<
        Record<string, TypedMap<CallWidgetState>>
    > = getVersion2Widgets(callWidgets);

    useEffect(() => {
        // This should log when we don't find the discriminator in the variables
        if (callWidgetVersion2 && variables?.NUMBER) {
            callWidgetVersion2
                .map((widget) => widget.toJS())
                .forEach((widget) => {
                    if (
                        !discriminatorIsPresent(
                            widget.config.discriminator,
                            variables,
                        )
                    ) {
                        addUserPhoneLogEntry(
                            `Discriminator ${
                                widget.config.discriminator
                            } from widget ${
                                widget.title
                            } not found among variables: ${JSON.stringify(
                                variables,
                            )}`,
                        );
                    }
                });
        }
    }, [callWidgetVersion2, variables]);

    return (
        <div
            className={twMerge(
                'flex flex-col items-center justify-center rounded-sm bg-gray-800 p-2',
                isOnHold ? 'text-gray-900' : 'text-gray-800',
            )}
            data-test-id={'large-call-item'}
        >
            {showLargeContact && (
                <>
                    <CallTitle
                        direction={direction}
                        failCause={failCause}
                        startTime={startTime}
                        isOnHold={isOnHold}
                        isEnded={isEnded}
                        useOmniErrandTerminologyForIncomingItem={activeCall?.get(
                            'isOmnichannelErrand',
                        )}
                    />

                    <CallAvatar
                        contact={contact}
                        size={'extraLarge'}
                        direction={direction}
                        isAccepted={isAccepted}
                        isOnHold={isOnHold}
                        isConference={isConference}
                    />

                    {isConference ? (
                        <TitleAndSubtitle
                            title={i18next.t('conference.call')}
                            subtitle={conferenceNumber}
                        >
                            <PinCode
                                meetingCode={conferenceMeetingCode}
                                pinCode={conferencePinCode}
                                onClick={getPinCode}
                            />
                        </TitleAndSubtitle>
                    ) : (
                        <TitleAndSubtitle
                            title={title.text}
                            subtitle={subtitle.text}
                        >
                            {extraInfo.text ? (
                                <div
                                    data-test-id="title-subtitle-third-row"
                                    className="text-gray-400"
                                >
                                    {extraInfo.text}
                                </div>
                            ) : null}
                        </TitleAndSubtitle>
                    )}
                </>
            )}

            {!showLargeContact && (
                <div className="my-2 flex w-full min-w-0 flex-1 items-center">
                    <AvatarDescription
                        contact={contact}
                        callCanvasMinimized={false}
                        direction={direction}
                        isAccepted={isAccepted}
                        isOnHold={isOnHold}
                        isConference={isConference}
                        conferenceMeetingCode={conferenceMeetingCode}
                        conferencePinCode={conferencePinCode}
                        getPinCode={getPinCode}
                        title={title.text}
                        subtitle={subtitle.text}
                    />
                    <div className="ms-auto me-2 flex items-center justify-center">
                        <CallTitle
                            direction={direction}
                            failCause={failCause}
                            startTime={startTime}
                            isOnHold={isOnHold}
                            isEnded={isEnded}
                            useOmniErrandTerminologyForIncomingItem={activeCall?.get(
                                'isOmnichannelErrand',
                            )}
                        />
                    </div>
                </div>
            )}

            <div className="my-1 box-border flex w-full flex-col gap-2 empty:m-0">
                <RedirectPresentationWidget activeCall={activeCall} />

                {externalSearchUrls?.map((urlData: ExternalSearchUrlData) => (
                    <ExternalSearchWidget
                        key={urlData.key}
                        url={urlData.url}
                        name={urlData.title}
                    />
                ))}

                {agentInfo && <EidWidget agentInfo={agentInfo} />}

                <OmniTicketWidget
                    activeCallKey={activeCall?.get('key')}
                    ticketKey={
                        omniTicketId ? `ticket-${omniTicketId}` : undefined
                    }
                />

                {centionDeeplink && (
                    <CentionErrandWidget deeplink={centionDeeplink} />
                )}

                {showCtiWidget && (
                    <CtiIntegrationWidget
                        direction={direction}
                        localNumber={localNumber}
                        remoteNumber={remoteNumber}
                    />
                )}
                {callWidgetVersion2
                    ?.map((widget) => widget.toJS().config)
                    .map(
                        (
                            { present, missing, discriminator }: Config,
                            index,
                        ) => (
                            <CallWidget
                                key={index}
                                variables={variables}
                                present={present}
                                missing={missing}
                                discriminator={discriminator}
                            />
                        ),
                    )}
            </div>

            <ButtonContainer
                onHangup={onHangup}
                onDecline={onDecline}
                onAnswer={onAnswer}
                onHold={onHold}
                onResume={onUnhold}
                onTransferStart={onTransferStart}
                onTransferComplete={onTransferComplete}
                onTransferCancel={onTransferCancel}
                useOmnichannelLook={activeCall?.get('isOmnichannelErrand')}
                onMerge={onMerge}
                conferenceToMeeting={conferenceToMeeting}
            />

            <div className="my-2 box-border w-full px-2 empty:m-0">
                <CoListeningSubjectBanner callId={callId} />
            </div>
        </div>
    );
};

export default React.memo(LargeCallItem);
